import type {
  ColorCode,
  ControlEntry,
  DeltaIndicator,
  DotStatus,
} from "./types";
import { UNRESOLVED_STATUSES } from "./constants";

export function getCanonicalFieldKey(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

// Backward-compatible alias while callers migrate to getCanonicalFieldKey.
export const normalizeFieldKey = getCanonicalFieldKey;

export function makeOverrideKey(
  entityType: string,
  entityId: number,
  fieldKey: string,
): string {
  return `${entityType}:${entityId}:${fieldKey}`;
}

export function parseNum(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

export function getDotClasses(status: DotStatus): string {
  if (status === "manual") return "bg-violet-500";
  if (status === "conflict") return "bg-red-500";
  if (status === "missing") return "bg-red-500";
  return "bg-emerald-500";
}

export function getDotClassesFromColorCode(
  colorCode?: ColorCode,
  fallbackStatus?: DotStatus,
): string {
  if (colorCode === "violet") return "bg-violet-500";
  if (colorCode === "orange_light") return "bg-orange-300";
  if (colorCode === "green_light") return "bg-emerald-300";
  if (colorCode === "green_dark") return "bg-emerald-600";
  if (colorCode === "red") return "bg-red-500";
  if (colorCode === "neutral") return "bg-slate-400";
  return getDotClasses(fallbackStatus ?? "valid");
}

export function getDotStatus(
  hasOverride: boolean,
  displayValue: string,
  control?: ControlEntry,
  controlStatus?: string,
): DotStatus {
  const normalize = (v: string | undefined) =>
    String(v ?? "")
      .trim()
      .toLowerCase();
  const status = normalize(
    String(control?.cellStatus ?? controlStatus ?? undefined),
  );
  const rawMatchStatus = normalize(control?.rawMatchStatus);

  const golden = normalize(control?.goldenValue);
  const trr = normalize(control?.trrValue);
  const pdf = normalize(control?.pdfValue);
  const hasGolden = golden !== "";
  const hasTrr = trr !== "";
  const hasPdf = pdf !== "";
  const trrEqGolden =
    hasGolden &&
    hasTrr &&
    compareReconciliationValues(control?.trrValue, control?.goldenValue);
  const pdfEqGolden =
    hasGolden &&
    hasPdf &&
    compareReconciliationValues(control?.pdfValue, control?.goldenValue);
  const allKnownEqual =
    hasGolden &&
    ((hasTrr && hasPdf && trrEqGolden && pdfEqGolden) ||
      (hasTrr && !hasPdf && trrEqGolden) ||
      (!hasTrr && hasPdf && pdfEqGolden));

  if (status === "manual") return "manual";
  if (!status && hasOverride) return "manual";
  if (
    rawMatchStatus === "missing_trr" ||
    rawMatchStatus === "missing_pdf" ||
    rawMatchStatus === "missing_both" ||
    rawMatchStatus === "ambiguous"
  ) {
    return "conflict";
  }
  if (rawMatchStatus === "matched") return "valid";
  if (allKnownEqual) return "valid";
  if (!displayValue || displayValue === "—") return "missing";
  return "valid";
}

function _parseArrForCompare(s: string): string[] | null {
  try {
    const p = JSON.parse(s);
    if (Array.isArray(p)) return p.map((x) => String(x).trim().toLowerCase());
  } catch {}
  try {
    const p = JSON.parse(s.replace(/'/g, '"'));
    if (Array.isArray(p)) return p.map((x) => String(x).trim().toLowerCase());
  } catch {}
  return null;
}

function _toArrayForCompare(s: string): string[] {
  return [s.toLowerCase()];
}

export function compareReconciliationValues(
  a: string | undefined | null,
  b: string | undefined | null,
  isArray?: boolean,
): boolean {
  const na = String(a ?? "").trim();
  const nb = String(b ?? "").trim();
  if (!na || !nb) return false;

  if (isArray) {
    const arrA = _parseArrForCompare(na) ?? _toArrayForCompare(na);
    const arrB = _parseArrForCompare(nb) ?? _toArrayForCompare(nb);
    if (arrA.length !== arrB.length) return false;
    const sortedA = [...arrA].sort();
    const sortedB = [...arrB].sort();
    return sortedA.every((v, i) => v === sortedB[i]);
  }

  const naL = na.toLowerCase();
  const nbL = nb.toLowerCase();
  const arrA = _parseArrForCompare(naL);
  const arrB = _parseArrForCompare(nbL);
  if (arrA && arrB) {
    if (arrA.length !== arrB.length) return false;
    const sortedA = [...arrA].sort();
    const sortedB = [...arrB].sort();
    return sortedA.every((v, i) => v === sortedB[i]);
  }
  const aNum = Number(naL.replace(/,/g, ""));
  const bNum = Number(nbL.replace(/,/g, ""));
  if (Number.isFinite(aNum) && Number.isFinite(bNum))
    return Math.abs(aNum - bNum) < 1e-9;
  return naL === nbL;
}

export function prepareValueForFormatter(
  value: unknown,
  isNumericColumn: boolean,
): unknown {
  if (typeof value !== "string") return value;
  if (!isNumericColumn) return value;
  return value.replace(/,/g, "").trim();
}

export function buildTableDeltaIndicator(
  control?: ControlEntry,
  fieldKey?: string,
): DeltaIndicator | null {
  if (!control) return null;
  const observedRaw =
    control.trrValue !== undefined &&
    control.trrValue !== null &&
    control.trrValue !== ""
      ? control.trrValue
      : control.pdfValue;
  const goldenRaw = control.goldenValue;
  if (!observedRaw || !goldenRaw) return null;
  const obsTxt = String(observedRaw).trim();
  const goldTxt = String(goldenRaw).trim();
  const obs = Number(obsTxt.replace(/,/g, ""));
  const gold = Number(goldTxt.replace(/,/g, ""));

  if (Number.isFinite(obs) && Number.isFinite(gold)) {
    const delta = obs - gold;
    const isMoic = String(fieldKey ?? "").toLowerCase() === "moic";
    const absoluteEpsilon = isMoic ? 1e-6 : 1e-9;
    const percentEpsilon = isMoic ? 0 : 0.05;
    if (Math.abs(delta) < absoluteEpsilon)
      return { label: "=", color: "success" };
    if (gold === 0)
      return {
        label: delta > 0 ? "▲" : "▼",
        color: delta > 0 ? "success" : "danger",
      };
    const pctValue = (delta / gold) * 100;
    if (Math.abs(pctValue) < percentEpsilon)
      return { label: "=", color: "success" };
    const pct = pctValue.toFixed(1);
    const sign = delta > 0 ? "+" : "";
    return {
      label: `${delta > 0 ? "▲" : "▼"} ${sign}${pct}%`,
      color: delta > 0 ? "success" : "danger",
    };
  }

  if (obsTxt.toLowerCase() === goldTxt.toLowerCase())
    return { label: "=", color: "success" };
  return { label: "≠", color: "danger" };
}

export function buildPreviousValueDeltaIndicator(
  currentValue: string,
  previousValue?: string,
): DeltaIndicator | null {
  if (!previousValue) return null;
  const cur = String(currentValue ?? "").trim();
  const prev = String(previousValue ?? "").trim();
  if (!cur || !prev) return null;

  const cNum = Number(cur.replace(/,/g, ""));
  const pNum = Number(prev.replace(/,/g, ""));
  if (Number.isFinite(cNum) && Number.isFinite(pNum)) {
    const delta = cNum - pNum;
    if (Math.abs(delta) < 1e-9) return { label: "=", color: "success" };
    if (pNum === 0)
      return {
        label: delta > 0 ? "▲" : "▼",
        color: delta > 0 ? "success" : "danger",
      };
    const pctValue = (delta / pNum) * 100;
    if (Math.abs(pctValue) < 0.05) return { label: "=", color: "success" };
    const pct = pctValue.toFixed(1);
    const sign = delta > 0 ? "+" : "";
    return {
      label: `${delta > 0 ? "▲" : "▼"} ${sign}${pct}%`,
      color: delta > 0 ? "success" : "danger",
    };
  }
  return cur.toLowerCase() === prev.toLowerCase()
    ? { label: "=", color: "success" }
    : { label: "≠", color: "danger" };
}

export function isUnresolvedStatus(status?: string): boolean {
  const s = String(status ?? "").toLowerCase();
  return (UNRESOLVED_STATUSES as readonly string[]).includes(s);
}

export function getIssueTypeLabel(
  reasonCode?: string,
  status?: string,
): string {
  const v = String(reasonCode ?? "").toLowerCase();
  if (v.startsWith("a") || v.includes("source") || v.includes("golden"))
    return "A - Source / Golden";
  if (v.startsWith("b") || v.includes("normal")) return "B - Normalization";
  if (v.startsWith("c") || v.includes("derived")) return "C - Derived field";
  if (v.startsWith("d") || v.includes("export") || v.includes("mapping"))
    return "D - Export / Mapping";
  const s = String(status ?? "").toLowerCase();
  if (s === "pending" || s === "needs_review") return "A - Source / Golden";
  return "Technical";
}

export function getPriority(
  status?: string,
  materialityAbs?: number | null,
): string {
  const s = String(status ?? "").toLowerCase();
  if (s === "pending" || s === "needs_review") {
    if ((materialityAbs ?? 0) >= 1_000_000) return "P0";
    return "P1";
  }
  if (s === "proposed") return "P2";
  return "OK";
}

export function buildPreviousIndicator(
  currentValue: string | undefined | null,
  previousValue: string | undefined | null,
): { label: string; tone: "default" | "success" | "warning" | "danger" } {
  const current = String(currentValue ?? "").trim();
  const previous = String(previousValue ?? "").trim();
  if (!previous) return { label: "", tone: "default" };
  if (current.toLowerCase() === previous.toLowerCase())
    return { label: "Same as previous", tone: "success" };

  const curNum = Number(current.replace(/,/g, ""));
  const prevNum = Number(previous.replace(/,/g, ""));
  if (Number.isFinite(curNum) && Number.isFinite(prevNum)) {
    const delta = curNum - prevNum;
    const sign = delta > 0 ? "+" : "";
    const pct =
      prevNum !== 0 ? `${((delta / prevNum) * 100).toFixed(1)}%` : "n/a";
    const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "=";
    return {
      label: `${arrow} ${sign}${new Intl.NumberFormat("en-US").format(delta)} (${pct})`,
      tone: delta > 0 ? "success" : delta < 0 ? "danger" : "default",
    };
  }

  return { label: "Changed vs previous", tone: "warning" };
}
